import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import wizardSchema from '../data/wizardSchema.json';
import type { WizardAnswers, ProjectDraft, ProjectDocument } from '../types/wizard';
import { WizardState } from '../types/wizard';
import { mapDocumentKeyToTypes } from '../utils/documentTypeMapper';

interface WizardStore {
  currentDraft: ProjectDraft | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  createDraft: () => Promise<string>;
  loadDraft: (id: string) => Promise<void>;
  saveAnswer: (stepIndex: number, key: string, value: string) => Promise<void>;
  uploadDocument: (draftId: string, type: string, file: File) => Promise<void>;
  deleteDocument: (draftId: string, documentId: string) => Promise<void>;
  submitProject: (draftId: string) => Promise<string>;
  clearDraft: () => void;
  
  // Auto-save
  autoSave: (draftId: string, answers: WizardAnswers, currentStep: number) => Promise<void>;
}

export const useWizardStore = create<WizardStore>((set, get) => ({
  currentDraft: null,
  isLoading: false,
  error: null,

  createDraft: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create a draft project
      const { data, error } = await supabase
        .from('projects')
        .insert({
          name: `Projet ${new Date().toLocaleDateString('fr-FR')}`,
          description: null,
          domain: 'SOCIAL', // Default value
          owner_id: user.id,
          status: 'A_PREPARER'
        })
        .select()
        .single();

      if (error) throw error;

      const draft: ProjectDraft = {
        id: data.id,
        currentStep: 1,
        answers: {},
        wizardState: WizardState.DRAFT,
        documents: [],
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      set({ currentDraft: draft, isLoading: false });
      return data.id;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  loadDraft: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          documents(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Parse wizard data from project description or use localStorage as fallback
      const wizardData = JSON.parse(localStorage.getItem(`wizard_${id}`) || '{}');

      // Map documents from database format to draft format
      const mappedDocuments = (data.documents || []).map((doc: any) => ({
        id: doc.id,
        type: doc.document_type, // Map document_type to type
        document_subtype: doc.document_subtype,
        filename: doc.filename,
        file_url: doc.file_url,
        file_size: doc.file_size,
        uploaded_at: doc.uploaded_at
      }));

      const draft: ProjectDraft = {
        id: data.id,
        currentStep: wizardData.currentStep || 1,
        answers: wizardData.answers || {},
        wizardState: wizardData.wizardState || WizardState.DRAFT,
        documents: mappedDocuments,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      set({ currentDraft: draft, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  saveAnswer: async (stepIndex: number, key: string, value: string) => {
    const { currentDraft } = get();
    if (!currentDraft) return;

    const updatedAnswers = { ...currentDraft.answers, [key]: value };
    const nextStep = Math.max(stepIndex + 1, currentDraft.currentStep);
    
    const updatedDraft = {
      ...currentDraft,
      answers: updatedAnswers,
      currentStep: nextStep,
      wizardState: nextStep > wizardSchema.steps.length ? WizardState.READY_FOR_DOCS : WizardState.IN_PROGRESS
    };

    set({ currentDraft: updatedDraft });
    
    // Auto-save to localStorage
    localStorage.setItem(`wizard_${currentDraft.id}`, JSON.stringify({
      currentStep: updatedDraft.currentStep,
      answers: updatedDraft.answers,
      wizardState: updatedDraft.wizardState
    }));

    // Save to database
    try {
      await get().autoSave(currentDraft.id, updatedAnswers, nextStep);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  },

  autoSave: async (draftId: string, answers: WizardAnswers, currentStep: number) => {
    try {
      // Update project with current progress
      const { error } = await supabase
        .from('projects')
        .update({
          description: JSON.stringify({ answers, currentStep, wizardState: currentStep > wizardSchema.steps.length ? WizardState.READY_FOR_DOCS : WizardState.IN_PROGRESS }),
          updated_at: new Date().toISOString()
        })
        .eq('id', draftId);

      if (error) throw error;
    } catch (error) {
      console.error('Database auto-save failed:', error);
    }
  },

  uploadDocument: async (draftId: string, type: string, file: File) => {
    set({ isLoading: true, error: null });
    try {
      console.log('=== UPLOAD START ===');
      console.log('Uploading document:', { draftId, type, fileName: file.name, fileSize: file.size });

      // Verify user authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        throw authError;
      }
      console.log('User authenticated:', { userId: user?.id, email: user?.email });

      // Verify project exists and user owns it
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('id, owner_id, name')
        .eq('id', draftId)
        .maybeSingle();

      if (projectError) {
        console.error('Project fetch error:', projectError);
        throw projectError;
      }
      if (!project) {
        throw new Error('Projet non trouvé');
      }
      console.log('Project verified:', project);

      // Map document key to type/subtype
      const { documentType, documentSubtype } = mapDocumentKeyToTypes(type);
      console.log('Mapped document:', { key: type, documentType, documentSubtype });

      // Upload file to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${draftId}/${type}_${Date.now()}.${fileExt}`;
      console.log('Uploading to storage:', fileName);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('project-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError;
      }
      console.log('Storage upload successful:', uploadData);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('project-documents')
        .getPublicUrl(fileName);
      console.log('Public URL generated:', publicUrl);

      // Save document metadata with both type and subtype
      console.log('Inserting document metadata to database...');
      const insertData = {
        project_id: draftId,
        filename: file.name,
        file_url: publicUrl,
        file_size: file.size,
        mime_type: file.type,
        document_type: documentType,
        document_subtype: documentSubtype,
        uploaded_by: user?.id,
        is_validated: null
      };
      console.log('Insert data:', insertData);

      const { data, error } = await supabase
        .from('documents')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      console.log('Document saved to database:', data);
      console.log('=== UPLOAD SUCCESS ===');

      // Update current draft
      const { currentDraft } = get();
      if (currentDraft) {
        const updatedDraft = {
          ...currentDraft,
          documents: [...currentDraft.documents, {
            id: data.id,
            type: data.document_type,
            document_subtype: data.document_subtype,
            filename: data.filename,
            file_url: data.file_url,
            file_size: data.file_size,
            mime_type: data.mime_type,
            uploaded_at: data.created_at
          }]
        };
        console.log('Updated draft with new document:', updatedDraft);
        set({ currentDraft: updatedDraft });
      }

      set({ isLoading: false });
    } catch (error: any) {
      console.error('Error uploading document:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deleteDocument: async (draftId: string, documentId: string) => {
    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      // Update current draft
      const { currentDraft } = get();
      if (currentDraft) {
        const updatedDraft = {
          ...currentDraft,
          documents: currentDraft.documents.filter(doc => doc.id !== documentId)
        };
        set({ currentDraft: updatedDraft });
      }
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  submitProject: async (draftId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { currentDraft } = get();
      if (!currentDraft) throw new Error('No draft found');

      // Validate all required documents are uploaded
      const requiredDocs = wizardSchema.requiredDocuments;
      for (const reqDoc of requiredDocs) {
        const hasDoc = currentDraft.documents.some(doc => doc.document_subtype === reqDoc.type.toUpperCase());
        if (!hasDoc) {
          throw new Error(`Document requis manquant: ${reqDoc.label}`);
        }
      }

      // Get project name from wizard answers
      const projectName = currentDraft.answers.nom_projet || generateProjectTitle(currentDraft.answers);

      // Update project with final data
      const { data, error } = await supabase
        .from('projects')
        .update({
          name: projectName,
          status: 'SOUMIS'
        })
        .eq('id', draftId)
        .select()
        .single();

      if (error) throw error;

      // Clear localStorage
      localStorage.removeItem(`wizard_${draftId}`);

      set({ currentDraft: null, isLoading: false });
      return data.id;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  clearDraft: () => {
    set({ currentDraft: null, error: null });
  }
}));

// Helper function to generate meaningful project titles
const generateProjectTitle = (answers: WizardAnswers): string => {
  const domaine = answers.domaine || 'ESS';
  const solution = answers.solution;
  const publicConcerne = answers.public_concerne;
  
  // Generate title based on answers
  if (solution && publicConcerne) {
    return `${solution} pour ${publicConcerne}`;
  } else if (solution) {
    return `${solution} - ${domaine}`;
  } else if (domaine) {
    return `Projet ${domaine}`;
  } else {
    return `Projet ESS ${new Date().toLocaleDateString('fr-FR')}`;
  };
}